import { stringify } from 'querystring';

class PbxService {
  /** @type {string} */
  baseURL;

  constructor(baseURL) {
    this.baseURL = baseURL;
    this.pages = [];
    this.pageContent = [];

    this.refreshes = {
      pageContent: false,
      services: false,
    };

    this.fetchOptions = {};

    if (process.server) {
      this.fetchOptions = {
        headers: {
          'User-Agent': `MPL-ROUTE-PLANNER/${process.env.VERSION} (+https://mpl-route-planner.${process.env.ENVIRONMENT}.parkos.dev)`,
        },
      };
    }
  }

  refresh() {
    this.refreshes.pageContent = true;
    this.refreshes.services = true;
  }

  generateQuery(path, query) {
    return `${this.baseURL.replace(/^\/|\/$/g, '')}/${path}/?${stringify(query, '&', '=', {
      arrayFormat: 'bracket',
      skipNull: true,
      skipEmptyString: true,
    })}`;
  }

  getPageContent(devtitle) {
    const self = this;

    if (!self.pageContent[devtitle] || self.refreshes.pageContent === true) {
      self.refreshes.pageContent = false;
      const url = this.generateQuery('content/pages', { devtitle });

      return new Promise((resolve, reject) => {
        fetch(url, this.fetchOptions)
          .then((res) => res.json())
          .then((response) => {
            self.pageContent[devtitle] = response;
            resolve(self.pageContent[devtitle]);
          })
          .catch((e) => {
            if (self.pageContent[devtitle] !== null) {
              resolve(self.pageContent[devtitle]);
            } else {
              reject(e);
            }
          });
      });
    }

    return new Promise((resolve) => {
      resolve(self.pageContent[devtitle]);
    });
  }

  /* eslint class-methods-use-this: "off" */
  async getHeaderAirportsConfiguration() {
    return {
      default: {
        columns: 4,
      },
      Canada: {
        columns: 3,
      },
      'United States': {
        columns: 3,
      },
    };
  }

  async getLanguages(limit = 50) {
    try {
      const url = this.generateQuery('languages', { limit });
      const { data } = await fetch(url, this.fetchOptions).then((res) => res.json());
      return data;
    } catch (e) {
      return e;
    }
  }

  async getTranslations(lang) {
    try {
      const url = this.generateQuery('content/translations', { lang });
      const data = await fetch(url, this.fetchOptions).then((res) => res.json());
      return data;
    } catch (e) {
      return e;
    }
  }

  async getAirports(lang, limit = 250, orderBy = 'locations_content.maintitle') {
    try {
      const url = this.generateQuery('content/airports', { lang, limit, orderBy });
      const { data } = await fetch(url, this.fetchOptions).then((res) => res.json());
      return data;
    } catch (e) {
      return e;
    }
  }

  async getAirportReviews(airport, lang, limit = 10) {
    return new Promise((resolve, reject) => {
      const url = this.generateQuery('content/airport/review', { lang, limit, airport });
      fetch(url, this.fetchOptions)
        .then((res) => res.json())
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          console.error('PBX error: ', error);
          reject(error);
        });
    });
  }

  async getAirportData(airport) {
    try {
      const url = this.generateQuery(`airports/${airport.id}/details`);
      const data = await fetch(url, this.fetchOptions).then((res) => res.json());
      return data;
    } catch (e) {
      return e;
    }
  }

  async getOpeningHours(lang) {
    try {
      const url = this.generateQuery('customer-service/opening-times.json', { lang });
      const response = await fetch(url, this.fetchOptions).then((res) => res.json());

      return response.data;
    } catch (error) {
      console.log('Error fetching opening hours:', error);
      throw error;
    }
  }

  async getPageTemplate(slug, lang) {
    try {
      const url = this.generateQuery('content/page-templates', { lang, slug });
      const data = await fetch(url, this.fetchOptions).then((res) => res.json());
      return data.data[lang];
    } catch (e) {
      return e;
    }
  }

  async getMerchants(locationId, languageId) {
    const response = await fetch(
      `${this.baseURL}/merchants?locationId=${locationId}&languageId=${languageId}`,
      this.fetchOptions,
    );
    return response.json();
  }

  async getMerchantContent(merchantId, locationId, languageId, parkType) {
    const response = await fetch(
      `${this.baseURL}/merchants/${merchantId}/content?locationId=${locationId}&languageId=${languageId}&parkType=${parkType}`,
      this.fetchOptions,
    );
    return response.json();
  }

  async getLocationRouteContent(locationId, languageId) {
    const response = await fetch(
      `${this.baseURL}/locations/${locationId}/route-content?languageId=${languageId}`,
      this.fetchOptions,
    );
    const { data } = await response.json();

    return data;
  }

  /**
   * @param key {string}
   * @returns {Promise<{lang: number, value: string}[]>}
   */
  async getTranslationValues(key) {
    const response = await fetch(`${this.baseURL}/translation/?key=${key}`, this.fetchOptions);
    return response.json();
  }

  async getParkingDetails(iorderid, email, languageId) {
    const response = await fetch(
      `${this.baseURL}/parkingDetails/?iorderid=${iorderid}&email=${email}&languageId=${languageId}`,
      this.fetchOptions,
    );

    return response.json();
  }
}

const pbxInstances = {};

function getPbxInstance(name, baseUrl, store, cookie) {
  pbxInstances[name] = new PbxService(baseUrl, store, cookie);
  return pbxInstances[name];
}

export default getPbxInstance;
